import type { FeatureEditSession } from '../../../edit/FeatureEditSession';
import { ChamferFeatureDefinitionModel } from '../../../definition/chamfer/ChamferFeatureDefinition';
import { ChamferFeaturePrototype } from '../../../prototype/chamfer/ChamferFeaturePrototype';
import { ChamferPreviewExecution } from './ChamferPreviewExecution';
import {
    FeaturePreviewExecutionHandler,
    type FeaturePreviewExecutionInput,
    type FeaturePreviewExecutionResult,
} from '../FeaturePreviewExecutionHandler';

/**
 * 倒角特征预览执行处理器。
 */
export class ChamferPreviewExecutionHandler extends FeaturePreviewExecutionHandler {
    /** 倒角预览执行器。 */
    private readonly execution: ChamferPreviewExecution;

    /**
     * @param execution 倒角预览执行器。
     */
    public constructor(execution: ChamferPreviewExecution) {
        super();
        this.execution = execution;
    }

    /**
     * @inheritdoc
     */
    public supports(input: FeaturePreviewExecutionInput): boolean {
        return input.featureKind === 'chamfer' && input.definition instanceof ChamferFeatureDefinitionModel;
    }

    /**
     * @inheritdoc
     * @throws 当预览输入中的定义不是倒角特征定义时。
     */
    public async execute(input: FeaturePreviewExecutionInput): Promise<FeaturePreviewExecutionResult> {
        if (!(input.definition instanceof ChamferFeatureDefinitionModel)) {
            throw new Error('倒角预览处理器收到的 definition 类型不正确');
        }

        const prototype = new ChamferFeaturePrototype({
            featureId: input.featureId ?? `preview:${Date.now()}`,
            definition: input.definition,
            bindings: input.bindings,
            sourceSketchId: null,
        });
        const previewResult = input.session
            ? await this.execution.executeEditPreview(
                input.session as FeatureEditSession<ChamferFeatureDefinitionModel, ChamferFeaturePrototype>,
                input.definition,
                { preferredItemRef: input.preferredItemRef },
            )
            : await this.execution.executeCreatePreview(input.definition, { preferredItemRef: input.preferredItemRef });
        return {
            meshData: previewResult.meshData,
            presentation: {
                blendGuides: previewResult.blendGuides ?? [],
            },
            prototype,
            draftDefinition: input.definition,
        };
    }
}
